/**
 * Microsoft Clarity session-recording bootstrap.
 *
 * Vue parity: `RW-ERP-Service/src/main.ts:8-60`. Called once from
 * `index.tsx`, mirroring where Vue's `main.ts` runs it — before the app
 * renders, on every page including the login screen. It reads whatever
 * `AuthManager.getUser()` returns AT THAT MOMENT: a login that happens
 * later in the same tab is not picked up until the next full page load,
 * exactly like Vue (Vue's `main.ts` only runs once per page load too).
 */
import Clarity from '@microsoft/clarity';

import { AuthManager } from 'config/auth.config';
import { CLARITY_TAGGED_EMAIL_DOMAIN, MICROSOFT_CLARITY_ID } from 'constants/clarity';

/**
 * The `@microsoft/clarity` SDK types `customSessionId` as `string`, but Vue
 * parity (see `initClarity` below) passes an arbitrary tag object through it
 * and Clarity accepts it at runtime. This narrows the call's actual accepted
 * shape once, at the boundary, instead of casting the argument itself
 * through `unknown` at the call site.
 */
type ClarityIdentify = (
  customId: string,
  customSessionId?: string | Record<string, unknown>,
  customPageId?: string,
  friendlyName?: string,
) => void;

/*
  The SDK ships `identify` as a plain property rather than a stable API
  surface, so the reinterpretation above is gated on a runtime check: if a
  future version drops or renames it, Clarity simply records without the
  identity tags instead of throwing on boot.
*/
const identify: ClarityIdentify =
  typeof Clarity.identify === 'function'
    ? (Clarity.identify as ClarityIdentify)
    : () => undefined;

/**
 * Sanitize an email (or arbitrary string) into a Clarity-safe custom user
 * id: the local part of an email address, alphanumerics only. Vue parity:
 * `main.ts`'s `getClarityUserId`.
 */
export function getClarityUserId(input: string): string {
  if (!input || typeof input !== 'string') return '';
  const value = input.includes('@') ? input.split('@')[0] : input;
  return value.replace(/[^a-zA-Z0-9]/g, '');
}

export function initClarity(): void {
  Clarity.init(MICROSOFT_CLARITY_ID);

  const email = AuthManager.getUser()?.email ?? '';
  const customUser = getClarityUserId(email);
  if (!customUser) return;

  // Vue passes `{ role: email }` as the `customSessionId` argument, not a
  // string, despite the SDK's typing — preserved verbatim here for Clarity
  // session parity with the Vue app.
  identify(customUser, { role: email }, customUser, customUser);

  if (email.endsWith(CLARITY_TAGGED_EMAIL_DOMAIN)) {
    Clarity.setTag('email', email);
    Clarity.setTag('user', customUser);
  }
}
